import {useRef, useState} from 'react'
import type {TFunction} from 'i18next'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PorQue} from '@/components/ui/por-que'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {PageHeading} from '@/components/page-heading'
import {ListEmptyState, SectionCard} from '@/components/section-card'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {
  hayIntentoPendiente,
  MIS_REQUISITOS_PAGE_SIZE,
  useAportar,
  useMiCumplimiento,
  useMisRequisitos,
  useRetirarAportado,
  type MiFila,
} from '@/hooks/empresa/mi_espacio/use-mi-cumplimiento'
import type {components} from '@/lib/api-schema'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'

type Estado = components['schemas']['Estado']

/** `Record<Estado, …>` y no un `Set`: un estado nuevo del contrato rompe el build en vez de caer al respaldo.
 * El respaldo sigue vivo porque la API puede ir por delante del bundle cacheado. */
const CON_FRASE_CORTA: Record<Estado, true> = {
  cumple: true,
  sugerido: true,
  falta_con_plazo: true,
  falta_sin_plazo: true,
  por_vencer: true,
  rechazado: true,
  sugerido_fecha_vencida: true,
  plazo_pasado: true,
  vencido: true,
  en_curso: true,
  no_le_corresponde: true,
  choque_restriccion: true,
}

const TONO: Record<string, 'green' | 'amber' | 'red' | 'neutral'> = {
  verde: 'green',
  amarillo: 'amber',
  rojo: 'red',
  en_curso: 'neutral',
  gris: 'neutral',
}

/** Lo que el trabajador no ve (cifras de la empresa, terceros, motivos ajenos) no se esconde acá:
 * el servidor no lo trae (E10-H13). */
export function MiCumplimientoPage() {
  const {businessId} = useParams()
  const {t} = useTranslation()
  const {data, isLoading, isError} = useMiCumplimiento(businessId)
  const [page, setPage] = useState(1)
  const requisitos = useMisRequisitos(businessId, page)
  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (isError || !data) {
    return (
      <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="mi-cumplimiento-error">
        {t('miCumplimiento.errorCarga')}
      </p>
    )
  }
  return (
    <div className="space-y-6">
      <PageHeading title={t('miCumplimiento.titulo')} />
      <SectionCard>
        <div className="space-y-1.5" data-testid="mi-cumplimiento-franja">
          <Tag tone={data.habilitada ? 'green' : 'red'}>
            {data.habilitada ? t('miCumplimiento.habilitada') : t('miCumplimiento.noHabilitada')}
          </Tag>
          <p className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
            {data.sin_cargo
              ? t('miCumplimiento.sinCargo')
              : t('miCumplimiento.cargo', {cargos: data.nombre_cargo.join(', ')})}
          </p>
          <p
            className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground"
            data-testid="mi-cumplimiento-pendientes">
            {data.retirado ? t('miCumplimiento.retirado') : t('miCumplimiento.pendientes', {count: data.pendientes})}
            {data.retirado && (
              <PorQue label={t('common.porQue')} testId="mi-cumplimiento-retirado-porque">
                <p>{t('miCumplimiento.retiradoPorque')}</p>
              </PorQue>
            )}
          </p>
        </div>
      </SectionCard>
      {requisitos.data && requisitos.data.total === 0 ? (
        <ListEmptyState title={t('miCumplimiento.vacio')} />
      ) : (
        <div className="space-y-3">
          <ul className="space-y-3" data-testid="mi-cumplimiento-filas">
            {(requisitos.data?.items ?? []).map((fila) => (
              <Fila key={fila.requisito_id} businessId={businessId!} fila={fila} />
            ))}
          </ul>
          {!!requisitos.data?.total && (
            <PaginationBar
              page={page}
              pageSize={MIS_REQUISITOS_PAGE_SIZE}
              total={requisitos.data.total}
              onPageChange={setPage}
            />
          )}
        </div>
      )}
      <PorQue
        label={t('miCumplimiento.noConfirmaLabel')}
        testId="mi-cumplimiento-no-confirma"
        contenidoTestId="mi-cumplimiento-no-confirma-contenido">
        <p>{t('miCumplimiento.noConfirmaUsted')}</p>
      </PorQue>
    </div>
  )
}

/** `no_le_corresponde` interpola motivo, quién y cuándo, que el sobre del trabajador no trae. */
function detalleConFecha(fila: MiFila, t: TFunction): string | undefined {
  if (fila.estado === null || fila.estado === 'no_le_corresponde') return undefined
  return t(`cumplimiento.estado.${fila.estado}`, {
    nombre: fila.nombre,
    fecha: fila.vence_el ? formatDateOnly(fila.vence_el) : '',
    plazo: fila.plazo ? formatDateOnly(fila.plazo) : '',
    evidencia: fila.evidencia_el ? formatDateOnly(fila.evidencia_el) : '',
    dias: fila.dias_para_vencer ?? 0,
  })
}

function Fila({businessId, fila}: {businessId: string; fila: MiFila}) {
  const {t} = useTranslation()
  // Cinturón de modo dios, solo de UI: la API conserva sus permisos.
  const godReadOnly = useGodModeReadOnly()
  const entrada = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const aportar = useAportar(businessId)
  const retirar = useRetirarAportado(businessId)
  const tieneFraseCorta = fila.estado !== null && fila.estado in CON_FRASE_CORTA
  const corto =
    fila.estado === null ? t(`miCumplimiento.motivoCorto.${fila.motivo}`) : t(`cumplimiento.estadoCorto.${fila.estado}`)
  // Sin frase corta, el detalle pasa a ser la frase visible y no se repite en el porqué.
  const detalle = detalleConFecha(fila, t)
  const frase =
    fila.estado === null
      ? t(`miCumplimiento.motivo.${fila.motivo}`)
      : tieneFraseCorta
        ? t(`miCumplimiento.frase.${fila.estado}`)
        : (detalle ?? t(`cumplimiento.estado.${fila.estado}`))
  return (
    // `text-sm` en el `li` porque `CeldaEstado` no pisa el tamaño del nombre.
    <li className="surface rounded-lg p-4 text-sm" data-testid={`mi-fila-${fila.requisito_id}`}>
      <CeldaEstado
        chip={
          <Tag tone={TONO[fila.color ?? 'gris'] ?? 'neutral'} className="w-fit">
            {corto}
          </Tag>
        }
        nombre={fila.nombre}
        meta={<span data-testid={`mi-frase-${fila.requisito_id}`}>{frase}</span>}
        porque={frase === detalle ? undefined : detalle}
        porqueTestId={`mi-detalle-${fila.requisito_id}`}
      />
      <div className="mt-2 space-y-0.5">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('miCumplimiento.porque')}
        </p>
        {/* Tal cual lo imprime la ficha del responsable, con la misma función del servidor. */}
        {fila.porque.map((renglon) => (
          <p key={`${renglon.clave}-${renglon.valor}`} className="text-xs text-muted-foreground">
            {renglon.valor}
          </p>
        ))}
      </div>
      {fila.aportado_id ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <p className="text-xs text-muted-foreground" data-testid={`mi-aportado-${fila.requisito_id}`}>
            {t('miCumplimiento.aportado')}
          </p>
          <Button
            variant="outline"
            size="sm"
            data-testid={`mi-retirar-${fila.requisito_id}`}
            disabled={retirar.isPending || godReadOnly}
            onClick={() => {
              setError(null)
              retirar.mutate(
                {requisitoId: fila.requisito_id, evidenceId: fila.aportado_id!},
                {onError: (err) => setError(translateApiError(err))}
              )
            }}>
            {t('miCumplimiento.retirar')}
          </Button>
          <p className="w-full text-xs text-muted-foreground">{t('miCumplimiento.retirarAviso')}</p>
        </div>
      ) : (
        fila.puede_aportar &&
        !godReadOnly && (
          <div className="mt-3 space-y-1.5">
            {/* Formato y tamaño se rechazan acá y en el servidor, antes de subir megas con mala señal. */}
            <input
              ref={entrada}
              type="file"
              className="hidden"
              accept={EVIDENCE_ACCEPT}
              data-testid={`mi-archivo-${fila.requisito_id}`}
              onChange={(evento) => {
                const archivo = evento.target.files?.[0]
                if (!archivo) return
                setError(null)
                if (!EVIDENCE_ALLOWED_TYPES.has(archivo.type)) return setError(t('errors.evidenceBadFormat'))
                if (archivo.size > EVIDENCE_MAX_BYTES) return setError(evidenceTooBig(t, archivo))
                aportar.mutate(
                  {requisitoId: fila.requisito_id, file: archivo},
                  {onError: (err) => setError(translateApiError(err))}
                )
              }}
            />
            <Button
              variant="outline"
              size="sm"
              data-testid={`mi-aportar-${fila.requisito_id}`}
              disabled={aportar.isPending}
              onClick={() => entrada.current?.click()}>
              {aportar.isPending ? t('miCumplimiento.aportando') : t('miCumplimiento.aportar')}
            </Button>
          </div>
        )
      )}
      {error && (
        <p className="notice-amber mt-2 rounded-lg px-3 py-2 text-sm" data-testid={`mi-error-${fila.requisito_id}`}>
          {error}{' '}
          {/* Si el archivo llegó y falló el registro, el reintento reusa el mismo destino: no duplica evidencia ni aviso. */}
          {hayIntentoPendiente(businessId, fila.requisito_id) && t('miCumplimiento.reintento')}
        </p>
      )}
    </li>
  )
}
